import { Head, Link, useForm, usePage } from "@inertiajs/react";
import { ArrowLeft, Building2, Upload } from "lucide-react";

import CompanyLogoUploader from "@/components/company-logo-uploader";
import InputError from "@/components/input-error";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AppLayout from "@/layouts/app-layout";
import { organizationCompaniesPath } from "@/routes";
import type { BreadcrumbItem, Company, SharedData } from "@/types";

interface CompanyNewProps {
  company: Company;
}

const fiscalStatusOptions = [
  { value: "responsable_inscripto", label: "Responsable Inscripto" },
  { value: "autonomo", label: "Autónomo" },
  { value: "monotributista", label: "Monotributista" },
  { value: "exento", label: "Exento" },
  { value: "no_alcanzado", label: "No Alcanzado" },
];

export default function CompanyNew({ company }: CompanyNewProps) {
  const { auth } = usePage<SharedData>().props;
  const organizationSlug = auth.current_organization?.slug ?? "";
  const companiesHref = organizationCompaniesPath(organizationSlug);
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Compañías", href: companiesHref },
    { title: "Nueva", href: "#" },
  ];
  const { data, setData, post, processing, errors, transform } = useForm<{
    name: string;
    legal_name: string;
    tax_id: string;
    fiscal_address: string;
    fiscal_status: string;
    logo: File | null;
  }>({
    name: company?.name ?? "",
    legal_name: company?.legal_name ?? "",
    tax_id: company?.tax_id ?? "",
    fiscal_address: company?.fiscal_address ?? "",
    fiscal_status: company?.fiscal_status ?? "",
    logo: null,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    transform((formData) => ({
      company: formData,
    }));

    post(companiesHref, { forceFormData: true });
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Nueva Compañía" />
      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href={companiesHref}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver a Compañías
            </Link>
          </Button>
          <h1 className="text-3xl font-bold">Nueva Compañía</h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Información de la compañía
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre comercial *</Label>
                  <Input
                    id="name"
                    placeholder="Ej: Mi Compañía S.A."
                    value={data.name}
                    onChange={(e) => setData("name", e.target.value)}
                    disabled={processing}
                    aria-invalid={!!errors.name}
                  />
                  <InputError message={errors.name} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="legal_name">Razón social *</Label>
                  <Input
                    id="legal_name"
                    placeholder="Ej: Mi Compañía Sociedad Anónima"
                    value={data.legal_name}
                    onChange={(e) => setData("legal_name", e.target.value)}
                    disabled={processing}
                    aria-invalid={!!errors.legal_name}
                  />
                  <InputError message={errors.legal_name} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tax_id">CUIT *</Label>
                  <Input
                    id="tax_id"
                    placeholder="Ej: 20-12345678-9"
                    value={data.tax_id}
                    onChange={(e) => setData("tax_id", e.target.value)}
                    disabled={processing}
                    aria-invalid={!!errors.tax_id}
                  />
                  <p className="text-xs text-muted-foreground">
                    Formato: XX-XXXXXXXX-X
                  </p>
                  <InputError message={errors.tax_id} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fiscal_status">Condición fiscal</Label>
                  <Select
                    value={data.fiscal_status || undefined}
                    onValueChange={(value) => setData("fiscal_status", value)}
                    disabled={processing}
                  >
                    <SelectTrigger id="fiscal_status">
                      <SelectValue placeholder="Seleccionar condición" />
                    </SelectTrigger>
                    <SelectContent>
                      {fiscalStatusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="fiscal_address">Dirección fiscal</Label>
                  <Input
                    id="fiscal_address"
                    placeholder="Ej: Calle Principal 123, CABA"
                    value={data.fiscal_address}
                    onChange={(e) => setData("fiscal_address", e.target.value)}
                    disabled={processing}
                  />
                </div>
              </div>

              <div className="border-t pt-6">
                <CompanyLogoUploader
                  controlledFile={data.logo}
                  onFileChange={(file) => setData("logo", file)}
                  errorMessage={errors.logo}
                  processing={processing}
                  initialLogoUrl={company?.logo_url ?? null}
                  hideSubmit
                />
              </div>

              <div className="flex gap-3 pt-4 border-t justify-end">
                <Button
                  type="button"
                  variant="outline"
                  disabled={processing}
                  asChild
                >
                  <Link href={companiesHref}>Cancelar</Link>
                </Button>
                <Button type="submit" disabled={processing} className="gap-2">
                  <Upload className="h-4 w-4" />
                  {processing ? "Guardando..." : "Crear compañía"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
